import { Reducers, ReduxReusableComponents, RRCProps, State } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import React, { ReactNode } from "react";
import { GanttAssignmentEntities } from "./GanttAssignmentEntityDescriptor";
import { SplitPaneExt } from "@crispico/foundation-react/components/ReactSplitPaneExt/ReactSplitPaneExt";
import { CenteredMetric, PieDatum, ResponsivePieExt } from "@crispico/foundation-react/components/nivoExt";
import { DummyToRememberPeopleToCast, EntityDescriptor, EntityEditorFormSimple, EntityTableSimple, EntityTableSimpleProps, EntityTableSimpleReducers, EntityTableSimpleState, FieldDescriptor, Utils } from "@crispico/foundation-react";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import _ from "lodash";
import { Segment, Button, Modal, Header, Icon } from "semantic-ui-react";
import { ModalExt, Severity } from "@crispico/foundation-react/components/ModalExt/ModalExt";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { IFormValuesHolder } from "@crispico/foundation-react/entity_crud/fieldEditors/FieldEditor";
import DropdownFieldEditor from "@crispico/foundation-react/entity_crud/fieldEditors/DropdownFieldEditor";
import { FieldDescriptorSettings, FieldInterval } from "@crispico/foundation-react/entity_crud/CrudSettings";
import { Cell, Column } from "fixed-data-table-2";

const START_ID = -10000; // ID-s for new added entities will start from here and decrease
const getFieldDescriptor = (fieldName: string) => {
    return new class extends FieldDescriptor {
        constructor() {
            super();
            this.name = fieldName;
            this.type = FieldType.defaultManyToOne;
        }

        renderFieldEditor(formikProps: IFormValuesHolder<any>, additionalFieldEditorProps?: DummyToRememberPeopleToCast): React.ReactElement {
            return <DropdownFieldEditor formikProps={formikProps} fieldDescriptor={this}></DropdownFieldEditor>
        }

        getLabel(getDefaultMeasurementUnitSymbol?: boolean, withMeasurementUnitSymbol?: boolean): string {
            return _msg(entityDescriptors["EquipmentAuthorization"].getField(fieldName).getLabel());
        }
    }
}
const equipmentAuthorizationGanttAssignmentTabEntityDescriptor = new EntityDescriptor({ name: "EquipmentAuthorizationGanttAssignmentTab" }, false)
    .addFieldDescriptor(getFieldDescriptor("equipment"))
    .addFieldDescriptor(getFieldDescriptor("address"));

type EquipmentAuthorizationGanttAssignmentTab = {
    id: number,
    equipment: { id: number, identifier: string, equipmentType?: { id: number, name: string } },
    address: { id: number, name: string },
}

export class GanttAssignmentEquipmentAuthorizationPageState extends State {
    pieData: PieDatum[] = [];
    tableEntities: EquipmentAuthorizationGanttAssignmentTab[] = [];
    enableSave: boolean = false;
    enableRevert: boolean = false;
    openModal: boolean = false;
}

export class GanttAssignmentEquipmentAuthorizationPageReducers<S extends GanttAssignmentEquipmentAuthorizationPageState = GanttAssignmentEquipmentAuthorizationPageState> extends Reducers<S> { };

type Props = RRCProps<GanttAssignmentEquipmentAuthorizationPageState, GanttAssignmentEquipmentAuthorizationPageReducers>
    & { entities: GanttAssignmentEntities, onSave: (entities: EquipmentAuthorizationGanttAssignmentTab[]) => void };

export class GanttAssignmentEquipmentAuthorizationPage extends React.Component<Props> {

    componentDidMount(): void {
        if (this.props.entities) {
            this.computeData();
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (!prevProps || !_.isEqual(prevProps.entities, this.props.entities)) {
            this.computeData();
        }
    }

    protected computeData() {
        const addresses = this.props.entities["Address"] || {};
        // Table data
        const tableEntities: EquipmentAuthorizationGanttAssignmentTab[] = [];
        Object.keys(this.props.entities["EquipmentAuthorization"] || {}).forEach(id => {
            const equipmentAuthorization = this.props.entities["EquipmentAuthorization"][Number(id)];
            if (!equipmentAuthorization.equipment || !equipmentAuthorization.address) {
                return;
            }
            const equipmentResource = (this.props.entities["EquipmentResource"] || {})[equipmentAuthorization.equipment.id];
            if (!equipmentResource) {
                return;
            }
            const equipmentType = (this.props.entities["EquipmentType"] || {})[equipmentResource.equipmentType.id];
            const address = addresses[equipmentAuthorization.address.id];
            if (!address) {
                return;
            }
            tableEntities.push({
                id: equipmentAuthorization.id,
                equipment: {
                    id: equipmentResource.id, identifier: equipmentResource.identifier,
                    equipmentType: (equipmentType ? { id: equipmentType.id, name: equipmentType.name } : undefined)
                },
                address: { id: address.id, name: address.name }
            })
        });
        // Pie data
        const pieData: PieDatum[] = [];
        const addressCountsMap: { [key: string]: number } = {};
        Object.keys(this.props.entities["Flight"] || {}).forEach(id => {
            const parking = this.props.entities["Flight"][Number(id)].parking;
            if (parking) {
                const address = addresses[parking.id];
                if (address) {
                    if (!addressCountsMap[address.name]) {
                        addressCountsMap[address.name] = 0;
                    }
                    addressCountsMap[address.name]++;
                }
            }
        });
        Object.keys(addressCountsMap).forEach(key => {
            pieData.push({ id: key, label: key, value: addressCountsMap[key] });
        });

        this.populateFormFieldOptions();
        this.props.r.setInReduxState({ tableEntities, pieData, enableRevert: false, enableSave: false });
    }

    protected populateFormFieldOptions() {
        const equipmentFieldIntervals: FieldInterval[] = [];
        Object.keys(this.props.entities["EquipmentResource"] || {}).forEach(id => {
            // only positive IDs, because negative IDs are for the new placeholder equipment resources that do not exist in DB yet
            if (Number(id) < 0) {
                return;
            }
            const equipment = this.props.entities["EquipmentResource"][Number(id)];
            const equipmentType = (this.props.entities["EquipmentType"] || {})[equipment.equipmentType.id];
            equipmentFieldIntervals.push({ from: equipment.id, label: `${equipment.identifier} (${equipmentType?.name})` });
        });
        equipmentAuthorizationGanttAssignmentTabEntityDescriptor.getField("equipment").fieldDescriptorSettings = { fieldIntervals: equipmentFieldIntervals.sort((a, b) => a.label!.localeCompare(b.label!)) } as unknown as FieldDescriptorSettings;
        const addressFieldIntervals: FieldInterval[] = [];
        Object.keys(this.props.entities["Address"] || {}).forEach(id => {
            const address = this.props.entities["Address"][Number(id)];
            addressFieldIntervals.push({ from: address.id, label: address.name });
        });
        equipmentAuthorizationGanttAssignmentTabEntityDescriptor.getField("address").fieldDescriptorSettings = { fieldIntervals: addressFieldIntervals.sort((a, b) => a.label!.localeCompare(b.label!)) } as unknown as FieldDescriptorSettings;
    }

    protected onSubmitForm(values: any) {
        if (!values.equipment || !values.address) {
            return;
        }
        const equipment = this.props.entities["EquipmentResource"][values.equipment];
        const address = this.props.entities["Address"][values.address];
        if (this.props.s.tableEntities.find(e => e.equipment.id === equipment.id && e.address.id === address.id)) {
            // already exists
            this.props.r.setInReduxState({ openModal: false });
            Utils.showGlobalAlert({ title: _msg("general.info"), message: _msg("GanttAssignmentEquipmentAuthorizationPage.sameEquipmentAndAddress.label", equipment.identifier, address.name), severity: Severity.INFO });
            return;
        }
        const equipmentType = (this.props.entities["EquipmentType"] || {})[equipment.equipmentType.id];
        const newEntity: EquipmentAuthorizationGanttAssignmentTab = {
            id: START_ID - (this.props.s.tableEntities.filter(e => e.id < 0).length),
            equipment: { ...equipment, equipmentType: equipmentType }, address: address
        };
        const tableEntities = [...this.props.s.tableEntities, newEntity];
        this.props.r.setInReduxState({ tableEntities, enableSave: true, enableRevert: true, openModal: false });
    }

    protected onDeleteEntity(entity: EquipmentAuthorizationGanttAssignmentTab) {
        this.props.r.setInReduxState({ tableEntities: this.props.s.tableEntities.filter(e => e.id !== entity.id), enableSave: true, enableRevert: true });
    }

    protected renderPie() {
        return <Segment className="wh100 overflow-hidden">
            <Header as="h4" dividing>{<><Icon name="map marker" /><>{_msg("GanttAssignmentEquipmentAuthorizationPage.flightsPerParking.label")}</></>}</Header>
            <div className="wh100 less-padding">
                <ResponsivePieExt data={this.props.s.pieData}
                    margin={{ top: 50, bottom: 50, right: 90, left: 90 }} layers={['arcs', 'arcLabels', 'arcLinkLabels', new CenteredMetric().get()]} colors={{ scheme: 'dark2' }}
                    activeOuterRadiusOffset={15} enableArcLabels={false} arcLinkLabel={(d) => `${d.label} (${d.value})`} />
            </div>
        </Segment>
    }

    protected renderTable() {
        return <div className="wh100 flex-container">
            <Segment className="buttonBar flex-center">
                <Button icon="plus" content={_msg("EquipmentAuthorization.label")} onClick={() => this.props.r.setInReduxState({ openModal: true })} />
                <Button primary disabled={!this.props.s.enableSave} content={_msg("general.save")} onClick={() => this.props.onSave(this.props.s.tableEntities)} />
                <Button disabled={!this.props.s.enableRevert} content={_msg("general.revert")} onClick={() => this.computeData()} />
            </Segment>
            <EquipmentAuthorizationGanttAssignmentTableSimpleRRC id="entityTableSimple_GanttAssignmentEquipmentAuthorizationRRC" entityDescriptor={entityDescriptors["EquipmentAuthorization"]} entitiesAsParams={this.props.s.tableEntities}
                columns={[{ name: "equipment", width: 250 }, { name: "equipment.equipmentType", width: 250 }, { name: "address", width: 250 }]} onDeleteEntity={(entity) => this.onDeleteEntity(entity)} />
            <ModalExt className="wh-auto" open={this.props.s.openModal} closeOnEscape closeOnDimmerClick closeIcon onClose={() => this.props.r.setInReduxState({ openModal: false })}>
                <Modal.Header>{_msg("EquipmentAuthorization.label")}</Modal.Header>
                <Modal.Content>
                    <EntityEditorFormSimple entityDescriptor={equipmentAuthorizationGanttAssignmentTabEntityDescriptor} entity={{}}
                        columnsVisibleMap={{ "equipment": true, "address": true }} onSubmitHandler={(values: any) => this.onSubmitForm(values)} />
                </Modal.Content>
            </ModalExt>
        </div>
    }

    render(): React.ReactNode {
        return <SplitPaneExt defaultSize="40%">
            {this.renderPie()}
            {this.renderTable()}
        </SplitPaneExt>
    }
}

export const GanttAssignmentEquipmentAuthorizationPageRRC = ReduxReusableComponents.connectRRC(GanttAssignmentEquipmentAuthorizationPageState, GanttAssignmentEquipmentAuthorizationPageReducers, GanttAssignmentEquipmentAuthorizationPage);

type EquipmentAuthorizationGanttAssignmentTableSimpleProps = EntityTableSimpleProps & { onDeleteEntity: (entity: EquipmentAuthorizationGanttAssignmentTab) => void };
class EquipmentAuthorizationGanttAssignmentTableSimple extends EntityTableSimple<EquipmentAuthorizationGanttAssignmentTableSimpleProps> {

    protected getContextMenuColumn(): ReactNode {
        return <Column width={120} key="menu" cell={props => {
            const { rowIndex } = props;
            return <Cell key={rowIndex} className="QualificationTableSimple_menuButtons">
                <Button icon={"close"} negative onClick={() => this.props.onDeleteEntity(this.getEntity(rowIndex))} />
            </Cell>
        }} />
    }

    protected renderColumns() {
        let columns = super.renderColumns();
        columns.unshift(this.getContextMenuColumn());
        return columns;
    }

}

export const EquipmentAuthorizationGanttAssignmentTableSimpleRRC = ReduxReusableComponents.connectRRC(EntityTableSimpleState, EntityTableSimpleReducers, EquipmentAuthorizationGanttAssignmentTableSimple);